import { orderBy } from 'lodash';

import { CHANNEL } from 'src/data/channel';
import { GENERATION_TOOL } from 'src/data/generationTool';
import { FILTER_ASSET_TYPE } from 'src/data/performance';

import { determineMetricTypes, getPerformanceGoalCalculate, KNOWN_METRIC } from './metrics';
import { DATA_SECTION } from './sections';

export const getGenerationToolFromChannelCopy = (channel, copy) => {
  switch (channel) {
    case CHANNEL.instagram:
      return GENERATION_TOOL.instagramCaptionRewrite;

    case CHANNEL.facebookPages:
      return GENERATION_TOOL.facebookPostRewrite;

    case CHANNEL.google:
      return copy.textType === FILTER_ASSET_TYPE.title
        ? GENERATION_TOOL.googleHeadlineProductRewrite
        : GENERATION_TOOL.googleDescriptionProductRewrite;

    case CHANNEL.facebook:
      return copy.textType === FILTER_ASSET_TYPE.title
        ? GENERATION_TOOL.facebookHeadlineProductRewrite
        : GENERATION_TOOL.facebookPrimaryTextProductRewrite;

    case CHANNEL.linkedin:
      return copy.textType === FILTER_ASSET_TYPE.title
        ? GENERATION_TOOL.linkedinHeadlineSingleImageRewrite
        : GENERATION_TOOL.linkedinIntroSingleImageRewrite;

    case CHANNEL.linkedinSocial:
      return GENERATION_TOOL.linkedinPostRewrite;

    case CHANNEL.twitterSocial:
      return GENERATION_TOOL.tweetRewrite;

    case CHANNEL.salesforce:
    case CHANNEL.marketo:
    case CHANNEL.hubspot:
    case CHANNEL.braze:
    case CHANNEL.iterable:
    case CHANNEL.eloqua:
      return copy.textType === FILTER_ASSET_TYPE.title
        ? GENERATION_TOOL.emailSubjectRewrite
        : GENERATION_TOOL.emailColdOutreachBodyRewrite;

    default:
      return null;
  }
};

export const createPerformanceDataToChartItemMapper = ({ goal, metricType }) => {
  const metricTypes = determineMetricTypes({
    goal,
    metricType,
  });
  const calculateGoal = getPerformanceGoalCalculate(goal);

  return (entry) => {
    const allInstances = entry.copy_performance_instances;

    // We care only about instances that have a start date and at least one suitable metric
    const metricsInstances = allInstances.filter(
      (inst) => inst.start_date && hasMetric(inst.performance_metrics, metricTypes)
    );
    if (metricsInstances.length === 0) {
      return null;
    }

    const sortedInstances = orderBy(metricsInstances, 'start_date', 'asc');
    const metrics = sortedInstances.flatMap((inst) => inst.performance_metrics);

    const impressions = aggregateImpressions(sortedInstances) || 0;
    const calculateMetric = (types) => aggregateMetrics(metrics, types);

    return {
      id: entry.id,
      text: entry.copy_text,
      copyType: entry.copy_type,
      date: sortedInstances[0].start_date,
      section: entry.generated_copy_asset ? DATA_SECTION.anyword : DATA_SECTION.other,
      goal: calculateGoal ? calculateGoal({ goal, metricType, impressions, calculateMetric }) : 0,
      value: impressions,
    };
  };
};

const aggregateImpressions = (instances) => {
  return instances.reduce((acc, item) => acc + item.impressions, 0);
};

const aggregateMetrics = (metrics, metricTypes = null) => {
  // Filter metrics by provided types, or, if no metric types are provided, use all metrics
  const filtered =
    !metricTypes || metricTypes.length === 0
      ? metrics
      : metrics.filter((item) => metricTypes.includes(item.type));

  // Sum up the values of the filtered metrics
  const sum = filtered.reduce((acc, item) => {
    const value = parseFloat(item.value) || 0;
    return acc + value;
  }, 0);

  // If CTR is requested, return the average CTR
  if (metricTypes?.includes(KNOWN_METRIC.ctr)) {
    return sum / filtered.length;
  }

  return sum;
};

const hasMetric = (metrics, metricTypes) => {
  return metrics.some((item) => metricTypes.includes(item.type));
};
